<template>
  <template v-if="mode === 'edit'">
    <div
      :class="[
        'py-2 unit-field relative',
        field.error ? 'my-2 px-2 rounded border-2 border-brand-red bg-brand-red bg-opacity-10' : ''
      ]"
      :name="field.name"
      :field-id="field.field_id"
      :required="field.required"
    >
      <!-- alert container -->
      <label for="input" class="block">
        {{ field.label }}
        <span v-if="field.required" class="text-brand-red">*</span>
      </label>

      <div class="flex flex-row justify-between">
        <div class="flex items-center w-2/3">
          <SingleLineInput
            @input="updateValue('street', $event.target.value)"
            placeholder="Street Address"
            :disabled="field.disabled"
            :value="street"
            :required="field.required"
          />
        </div>
        <div class="flex items-center w-1/4">
          <SingleLineInput
            @input="updateValue('unit', $event.target.value)"
            placeholder="Unit #"
            :disabled="field.disabled"
            :value="unit"
            :required="field.required"
          />
        </div>
      </div>

      <div class="flex flex-row justify-between">
        <div class="flex items-center w-1/3">
          <SingleLineInput
            @input="updateValue('city', $event.target.value)"
            placeholder="City"
            :disabled="field.disabled"
            :value="city"
            :required="field.required"
          />
        </div>
        <div class="flex items-center w-1/4">
          <DropDown
            :options="stateOptions"
            placeholder="State"
            :disabled="field.disabled"
            :required="field.required"
            @select="updateValue('state', $event.value)"
            ref="state"
          ></DropDown>
        </div>
        <div class="flex items-center w-1/3">
          <SingleLineInput
            placeholder="Zip"
            @input="updateValue('zip', $event.target.value)"
            :disabled="field.disabled"
            :value="zip"
            :required="field.required"
            mask="zipcode"
          />
        </div>
      </div>

      <template v-if="field.help_text">
        <p class="text-brand-mid-gray italic text-xs">{{ field.help_text }}</p>
      </template>
      <template v-if="field.error">
        <p class="text-brand-red py-1">{{ errorMessage }}</p>
      </template>
      <div
        class="absolute border border-brand-mid-gray rounded-sm bg-white flex flex-col top-28 w-4/5 md:w-1/2 md:top-20 left-2 z-10"
        v-if="showRecommendation"
      >
        <template v-if="isApplicationTemplateLoading">
          <div class="flex py-10 items-center justify-center">
            <SmallLoading />
          </div>
        </template>
        <template v-else>
          <template v-if="getApplicationTemplateAddressSuggestions">
            <div
              class="flex justify-end pt-1 px-1"
              v-if="getApplicationTemplateAddressSuggestions.length > 0"
            >
              <XIcon
                class="w-5 h-5 text-brand-gray cursor-pointer"
                @click="showRecommendation = false"
              />
            </div>
            <div
              class="flex items-center py-2 px-2 cursor-pointer hover:bg-brand-light-gray address-suggestion"
              v-for="suggestion in getApplicationTemplateAddressSuggestions"
              :key="suggestion.label"
              @click="selectSuggestion(suggestion)"
            >
              <div class="flex flex-col">
                <p class="text-sm py-0.5">{{ suggestion.label.split(",")[0] }}</p>
                <p class="text-xs text-brand-mid-gray">
                  {{ suggestion.city }}, {{ suggestion.stateCode }} {{ suggestion.postalCode }}
                  {{ suggestion.countryCode }}
                </p>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </template>
  <template v-if="mode === 'view'">
    <div class="flex items-center md:w-1/2 py-1 px-1">
      <p>{{ field.label }}</p>
    </div>
    <div class="flex flex-col md:w-1/2 py-1 px-1">
      <template v-if="street">
        <p class="font-semibold">
          {{ `${street}${unit ? ` ${unit}` : ""}` }}
        </p>
        <p class="font-semibold">
          {{ `${city}, ${state} ${zip}` }}
        </p>
      </template>
      <template v-else>
        <p class="font-semibold">Not entered</p>
      </template>
    </div>
  </template>
  <template v-if="mode === 'form_edit'">
    <div class="flex flex-col w-full">
      <div class="flex flex-row justify-between">
        <div class="flex items-center w-2/3">
          <SingleLineInput placeholder="Street Address" :disabled="true" />
        </div>
        <div class="flex items-center w-1/4">
          <SingleLineInput placeholder="Unit #" :disabled="true" />
        </div>
      </div>

      <div class="flex flex-row justify-between">
        <div class="flex items-center w-1/3">
          <SingleLineInput placeholder="City" :disabled="true" />
        </div>
        <div class="flex items-center w-1/4">
          <DropDown :options="stateOptions" placeholder="State" :disabled="true"></DropDown>
        </div>
        <div class="flex items-center w-1/3">
          <SingleLineInput placeholder="Zip" :disabled="true" />
        </div>
      </div>
      <TextDisplayEditToggle
        :helpText="field.help_text"
        :type="'field_subtext'"
        @input="updateHelpText"
        :disabled="field.editable === false"
      />
    </div>
  </template>
</template>
<script>
import _ from "lodash"
import { mapGetters } from "vuex"
import stateOptions from "@/constants/states"

import { XIcon } from "@heroicons/vue/outline"

import { APPLICATION_TEMPLATE_SEARCH_ADDRESS } from "@/store/types"

export default {
  components: {
    XIcon
  },
  props: {
    field: Object,
    mode: {
      type: String
    },
    stage: {
      type: String
    }
  },
  emits: ["update", "validated"],
  computed: {
    ...mapGetters(["getApplicationTemplateAddressSuggestions", "isApplicationTemplateLoading"]),
    isValid() {
      if (!this.field.required) {
        return true
      }

      // TODO: Add address validation here
      // if (this.field.value.length >= 5)
      if (!this.field.value) {
        return false
      }

      return this.isValidAddress
    },
    isValidAddress() {
      return !!this.street && !!this.city && !!this.state && !!this.zip
    }
  },
  watch: {
    // Needed to make ctrl+8 hot key work
    "field.value": {
      handler(val) {
        let address

        if (val) {
          if (typeof val === "string") {
            address = JSON.parse(val)
          } else {
            address = val
          }
          this.street = address.street
          this.unit = address.unit
          this.city = address.city
          this.state = address.state
          this.zip = address.zip

          if (this.state && this.mode == "edit" && this.$refs.state && !this.$refs.state.selected) {
            this.$refs.state.selected = this.stateOptions.find((s) => s.value === address.state)
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    if (this.field.name === "unit" && this.$route.query.p) {
      const property = JSON.parse(atob(this.$route.query.p))

      const that = this

      Object.keys(property).forEach((k) => {
        if (k === "state") {
          that.$refs.state.selected = that.stateOptions.find((s) => s.value === property[k])
        } else {
          that.updateValue(k, property[k])
        }
      })

      this.showRecommendation = false
    }
  },
  data() {
    return {
      showRecommendation: false,
      stateOptions,
      street: "",
      unit: "",
      city: "",
      state: "",
      zip: "",
      errorMessage: ""
    }
  },
  methods: {
    updateHelpText(ev) {
      // eslint-disable-next-line vue/no-mutating-props
      this.field.help_text = ev.target.value
    },
    updateField() {
      const { street, unit, city, state, zip } = this

      if (this.isValidAddress) {
        this.$emit(
          "update",
          JSON.stringify({
            street,
            unit,
            city,
            state,
            zip
          })
        )
      }
    },
    updateValue(type, val) {
      this[type] = val

      if (val) {
        if (type === "street") {
          if (val.length >= 3) {
            this.showRecommendation = true
          }
        }
      } else {
        this.showRecommendation = false
      }
      if (this.showRecommendation) {
        this.searchAddress()
      }

      this.updateField()
    },
    validateField() {
      if (!this.isValid) {
        if (this.field.required && !this.field.value) {
          this.errorMessage = "This field is required."
        } else {
          this.errorMessage = "Please enter a valid address."
        }
      } else {
        this.errorMessage = ""
      }
    },
    searchAddress: _.debounce(async function () {
      await this.$store.dispatch(APPLICATION_TEMPLATE_SEARCH_ADDRESS, {
        searchTerm: this.street
      })

      if (
        !this.getApplicationTemplateAddressSuggestions ||
        !this.getApplicationTemplateAddressSuggestions.length
      ) {
        this.showRecommendation = false
      }
    }, 500),
    selectSuggestion(suggestion) {
      this.street = `${suggestion.houseNumber} ${suggestion.street}`
      this.city = suggestion.city
      this.state = suggestion.stateCode
      this.zip = suggestion.postalCode.split("-")[0]
      this.$refs.state.selected = this.stateOptions.find((s) => s.value === suggestion.stateCode)

      this.showRecommendation = false
      this.$emit("validated", suggestion)

      this.updateField()
    }
  }
}
</script>
<style lang="scss" scoped></style>
